import { Avatar, EditPencilIcon, Icon, uploadResizedImage, ChildMark, CharacterPicker, characterPatch } from "../components/Common.jsx";
import { AVATARS, DEFAULT_SPENDING_CAP_GBP, uid } from "../lib/constants.js";
import { effectiveAge, estimateSchoolYear, resolveSchoolYear, SCHOOL_YEAR_OPTIONS } from "../lib/age.js";
import { fmtPence, seedNewChildDefaults, todayStr } from "../lib/utils.js";
import { clampApr } from "../lib/vaultInterest.js";
import { t as ct } from "../lib/content.js";
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// hideStartingBalance: true only when this renders inside the setup Wizard (see
// Wizard.jsx) — a brand-new household has no meaningful "starting balance" concept yet
// (nothing's been earned or set up), so the field is noise there. The ordinary Kids tab
// in Parent Mode still shows it, since a parent adding a sibling later to an established
// household may genuinely want to seed a starting balance (e.g. matching an existing
// child's). tokenBalance simply defaults to 0 when the field is hidden — parseInt("")
// already falls back to 0 in addChild() below, so no extra branching is needed there.
// forwardRef (Aug 2026): a parent whose child-edit form has unsaved changes shouldn't be
// able to silently lose them by switching Parent Mode tabs or exiting to Kid Mode — both of
// those live in App.jsx, well outside this component's own tree, so there's no prop path
// for them to just ask "is there unsaved data right now?" the normal way. This exposes a
// small imperative surface instead — hasUnsavedChanges/trySave/discard — so App.jsx (via
// ParentView, see its own comment) can check before it navigates, without this component
// needing to know anything about tabs or modes, and without lifting childDraft (and
// everything else it's entangled with — goals, avatar editing, bank setup) out of here.
// singleChildId (R-B addendum, Resolution 1): when set, renders only that one child, its
// card is always expanded (no expand/collapse chrome — the internal expandedId toggle is
// bypassed for display), and it's mounted by the Kids tab as this page's header/identity
// block. Left unset, behaviour is byte-identical to before (the wizard's list-mode mount).
export const ProfilesTab = React.forwardRef(function ProfilesTab({ state, update, updateAndSave, code, showToast, setActiveChildId, addTransaction, hiddenIds, onToggleVisibility, mutedIds, onToggleMute, hideStartingBalance, onOpenHouseholdMoney, autoExpandNewChild = true, singleChildId, onChildAdded }, ref) {
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [character, setCharacter] = useState("pg-base"); // LB-168: the child's identity
  const [startBal, setStartBal] = useState("");
  const [age, setAge] = useState("");
  // Quick-create school year (Aug 2026): live-estimated from age as it's typed, exactly
  // like the per-child edit view's dropdown defaults — a parent can accept the estimate
  // as-is or pick a different one. "Touched" stops the auto-update the moment they've
  // picked something themselves, so a later age correction doesn't silently overwrite an
  // explicit choice. Starts on the browser's own natural default (first option) rather
  // than "" so the controlled <select> and the real DOM never disagree before any age is
  // typed — see estimateSchoolYear's NaN guard in age.js for why an empty age can't drive
  // this directly.
  const [addSchoolYear, setAddSchoolYear] = useState(SCHOOL_YEAR_OPTIONS[0]);
  const [addSchoolYearTouched, setAddSchoolYearTouched] = useState(false);
  const [editingAvatarFor, setEditingAvatarFor] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [childDraft, setChildDraft] = useState(null); // { childId, name, tokenBalance, vaultBalance, age, schoolYear }
  // Set to the id of a child a parent tried to switch INTO editing while a different
  // child's edits were still unsaved (Aug 2026) — see requestEditChild below. Distinct
  // from childDraft itself: this only ever holds the id of the child they were trying to
  // move TO, so the confirm dialog can offer "save then switch" without losing track of
  // where they were headed.
  const [unsavedPromptFor, setUnsavedPromptFor] = useState(null);
  const [confirmingDeleteFor, setConfirmingDeleteFor] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // Unique ids for the Add Child form's fields, so their <label>s can be properly
  // associated via htmlFor — needed for screen readers and for any tooling (Playwright's
  // getByLabel, browser autofill, etc.) that identifies a field by its label rather than
  // by placeholder text, which these fields don't have. useId() keeps them collision-free
  // even though ProfilesTab can render more than once on a page (e.g. reused inside the
  // setup wizard).
  const addChildNameId = useId();
  const addStartBalId = useId();
  const addAgeId = useId();
  const addSchoolYearId = useId();

  function updateChildAvatar(childId, patch) {
    update((s) => {
      const child = s.profiles.find((p) => p.id === childId);
      if (!child) return s;
      const { characters, ...rest } = patch || {}; // LB-168: merge the nested characters object
      Object.assign(child, rest);
      if (characters) child.characters = { ...(child.characters || { owned: [], equipped: null }), ...characters };
      return s;
    });
  }
  async function handlePhoto(childId, e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setPhotoError("");
    try {
      const url = await uploadResizedImage(code, file);
      updateChildAvatar(childId, { avatarPhoto: url });
      setEditingAvatarFor(null);
    } catch (err) {
      setPhotoError(err.message || "Couldn't use that photo.");
    }
  }

  function addChild() {
    if (!name.trim()) return;
    // Age is mandatory (2026-08 decision) — every subsystem that adapts to age (Maths
    // Workout bands, Vocabulary categories, Spelling auto-serve, the Daily Question) was
    // silently guessing a different fallback band when it was missing, which is worse than
    // asking once at setup. This form is reused inside the onboarding Wizard (see
    // Wizard.jsx), so the requirement applies there too.
    if (age.trim() === "") { showToast(ct("toast.age_required", "Age is needed to pick the right difficulty level"), "bad"); return; }
    const id = uid();
    const ageVal = Math.max(0, Math.min(25, parseInt(age, 10) || 0));
    // addSchoolYear is always a real value by this point — the age field can't be blank to
    // get here, so the live estimate (or the parent's own pick) has already populated it.
    // Stored as an explicit, parent-confirmed value either way, same as the per-child edit
    // view — accepting the estimate as shown still counts as confirming it.
    update((s) => {
      s.profiles.push({ id, name: name.trim(), avatar, ...characterPatch(character), characters: { owned: [], equipped: character === "pg-base" ? null : character }, age: ageVal, ageSetAt: todayStr(), schoolYear: addSchoolYear || null, tokenBalance: parseInt(startBal, 10) || 0, vaultBalancePence: 0, earnsVaultInterest: true, goals: [], spendingCap: { enabled: true, weeklyGBP: DEFAULT_SPENDING_CAP_GBP } });
      // W1.2 gap fix (Aug 2026): seed explicit, exchange-rate-scaled reward defaults NOW,
      // instead of letting migrateState backfill the raw base values on the next load —
      // see seedNewChildDefaults in lib/utils.js for the full story. Also creates the
      // child's learningContent record (previously done inline right here).
      seedNewChildDefaults(s, id);
      return s;
    });
    setActiveChildId(id);
    if (autoExpandNewChild) setExpandedId(id);
    setName(""); setStartBal(""); setAge(""); setShowAdd(false);
    setAddSchoolYear(SCHOOL_YEAR_OPTIONS[0]); setAddSchoolYearTouched(false);
    // LB-129: a child added OUTSIDE setup gets the set-it-up-for-me / take-me-through-it
    // choice (ParentView mounts NewChildSetupSheet). The wizard passes no handler — its
    // own steps do that job.
    if (onChildAdded) onChildAdded(id);
  }
  function removeChild(id) {
    update((s) => {
      s.profiles = s.profiles.filter((p) => p.id !== id);
      s.transactions = s.transactions.filter((t) => t.childId !== id);
      s.activities = s.activities.filter((a) => a.childId !== id);
      s.requests = s.requests.filter((r) => r.childId !== id);
      s.habits = (s.habits || []).filter((h) => h.childId !== id);
      s.habitLog = (s.habitLog || []).filter((l) => l.childId !== id);
      s.experienceRewards = (s.experienceRewards || []).filter((r) => r.childId !== id);
      delete s.learningContent[id];
      return s;
    });
  }
  function startEditChild(p) {
    setChildDraft({
      childId: p.id,
      name: p.name,
      // Effective age, not the raw stored number — the parent always sees and edits the
      // child's CURRENT age (lib/age.js). Saving below restamps ageSetAt, so leaving this
      // field untouched re-anchors the same effective age to today (harmlessly correct)
      // rather than rewinding the growth clock to the original entry.
      age: effectiveAge(p) != null ? String(effectiveAge(p)) : "",
      // resolveSchoolYear, not p.schoolYear directly — opens the dropdown on the live
      // estimate for a profile that's never had one confirmed, so the field is never blank.
      // saveChildEdits below writes whatever ends up here as the real, explicit value
      // (parent-confirmed from this point on, even if it happens to match what the
      // estimate would already have said) — see age.js's resolveSchoolYear for why that
      // distinction matters (the "(estimated)" label elsewhere on this card).
      schoolYear: resolveSchoolYear(p) || "",
    });
    // Age (and everything else edit-related below it) only renders inside the expanded
    // body — force the card open here so entering edit mode always shows every editable
    // field at once, rather than only name/token/vault balance (which live in the always-
    // visible collapsed header) if the card happened to be collapsed when Edit was tapped.
    setExpandedId(p.id);
  }
  // Wraps startEditChild with an unsaved-changes guard (Aug 2026) — only one child's edits
  // can be in progress at once (childDraft holds a single id), so switching straight to
  // editing a DIFFERENT child would silently throw away whatever the first one had typed.
  // Same-child re-clicks of "Edit" (already editing p) and the ordinary first-time case
  // (nothing being edited yet) both skip the prompt entirely and go straight through.
  function requestEditChild(p) {
    if (childDraft && childDraft.childId !== p.id) {
      setUnsavedPromptFor(p.id);
      return;
    }
    startEditChild(p);
  }
  function updateDraftField(field, value) {
    setChildDraft((d) => (d ? { ...d, [field]: value } : d));
  }
  function saveChildEdits() {
    if (!childDraft) return false;
    const { childId, name, age, schoolYear } = childDraft;
    const child = state.profiles.find((p) => p.id === childId);
    if (!child) { setChildDraft(null); return false; }
    // Enforced here too, not just on Add — this is the backfill path for any child created
    // before age became mandatory. A parent who opens edit on an ageless profile must supply
    // one before the save goes through.
    if (age.trim() === "") { showToast(ct("toast.age_required", "Age is needed to pick the right difficulty level"), "bad"); return false; }
    renameChild(childId, name);
    const ageVal = Math.max(0, Math.min(25, parseInt(age, 10) || 0));
    // schoolYear saved as-is, even if it happens to equal what the estimate would already
    // have shown — from this point it's an explicit, parent-confirmed value (drives the
    // "(estimated)" label going away) rather than a live guess, per age.js's resolveSchoolYear.
    update((s) => { const c = s.profiles.find((p) => p.id === childId); if (c) { c.age = ageVal; c.ageSetAt = todayStr(); c.schoolYear = schoolYear || null; } return s; });
    setChildDraft(null);
    showToast(ct("toast.changes_saved", "Changes saved"), "good");
    return true;
  }

  // The imperative surface itself — see the component's own top comment for why this
  // exists instead of a normal prop path. discard() deliberately mirrors exactly what the
  // in-card "Cancel editing" button already does (setChildDraft(null) + clearing any open
  // avatar-photo panel), so there's exactly one definition of "what discarding means," not
  // two that could drift apart.
  const addFormRef = useRef(null);
  React.useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => !!childDraft,
    trySave: () => saveChildEdits(),
    discard: () => { setChildDraft(null); setEditingAvatarFor(null); },
    // The ＋ chip in the Kids tab's switcher row (Alan, 4 Sep: it did nothing when you were
    // already on Kids — goAddChild only switched tabs). Opens the form and brings it up.
    openAddChild: () => {
      setShowAdd(true);
      setTimeout(() => { try { addFormRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }); addFormRef.current?.querySelector("input")?.focus(); } catch {} }, 30);
    },
  }));

  function renameChild(childId, nameStr) {
    const trimmed = nameStr.trim();
    if (!trimmed) return;
    update((s) => { const child = s.profiles.find((p) => p.id === childId); if (child) child.name = trimmed; return s; });
  }

  // (R-B addendum, Resolution 3) setChildBalance/setChildVaultBalance were deleted here
  // along with the token/vault balance edit fields they backed — balance corrections now go
  // through Manage balances (Kids tab's "Do now" group), which writes a real, reasoned
  // transaction instead of a silent overwrite.
  // (Alan, Aug 2026) toggleBankAccount / handleBankToggle / confirmBankSetup /
  // cancelBankSetup were deleted here, along with the bank-setup amount modal they drove.
  // The Savings Vault is a permanent part of every child's profile now, so there is no
  // enable/disable moment left — and therefore no "starting balance" prompt to attach to it.
  // The vault balance field in the Balances group below is simply always available instead.
  //
  // What remains is the per-child INTEREST opt-out, which the vault toggle used to provide
  // as a side effect. Missing field reads as ON, matching migrateState's default.
  function earnsVaultInterest(child) {
    return child?.earnsVaultInterest !== false;
  }
  function toggleEarnsInterest(childId) {
    update((s) => {
      const c = s.profiles.find((x) => x.id === childId);
      if (c) c.earnsVaultInterest = !earnsVaultInterest(c);
      return s;
    });
  }

  return (
    <div className="flex flex-col gap-2 lb-form">
      {(singleChildId ? state.profiles.filter((p) => p.id === singleChildId) : state.profiles).map((p) => (
        <div key={p.id} data-testid="child-card" className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          {/* Collapsed header — a fixed, non-editable summary strip at every state (Aug
              2026 redesign). It never itself becomes an input; the single "Edit" pill
              inside the expanded body below is the only way into editing anything,
              including the name — see the Profile section further down. Kept: the avatar
              is a plain glance-only image here (no floating pencil badge) and the visibility
              toggle stays exactly where it was — both per Alan's sign-off on the redesign.
              In singleChildId mode (R-B addendum Resolution 1) this card is the Kids tab's
              header/identity block — always expanded, no collapse chrome, so the click
              handler and the ▲/▼ indicator both drop out entirely. */}
          {/* R-C polish (Aug 2026, punch-list 11.3): the singleChildId card now collapses
              by default too — the strip alone (name · balances · visibility · Edit) is the
              Kids tab's whole identity block until tapped open, so the page leads with
              Quick actions instead of a full-height card. Tap anywhere on the strip to
              expand; Edit jumps straight into editing (startEditChild force-expands). */}
          {/* Two rows (Alan, 3 Oct, option A). Name, balances, Edit and the visibility toggle
              shared one row that could not shrink on either side, so longer balances ("170
              tokens · £89.53 in Savings Vault") broke it. Row 1 is who and how much, with Edit
              and the chevron fixed at the end; the balances wrap under the name and nothing
              else moves. Row 2, under a hairline, is Show on this device and its toggle. */}
          <div className="mb-3 cursor-pointer" onClick={() => setExpandedId((v) => (v === p.id ? null : p.id))} data-testid="child-card-strip">
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0"><ChildMark profile={p} size="md" /></span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-700 text-sm break-words">{p.name}</div>
                <div className="text-xs text-slate-400 break-words">{p.tokenBalance} tokens · {fmtPence(p.vaultBalancePence)} in Savings Vault</div>
              </div>
              {singleChildId && expandedId !== p.id && !childDraft && (
                <button
                  className="text-slate-600 text-xs font-bold bg-slate-100 rounded-lg px-3 py-1.5 flex-shrink-0"
                  onClick={(e) => { e.stopPropagation(); requestEditChild(p); }}
                >{ct("profiles.edit_button", "Edit")}</button>
              )}
              <span className="text-slate-400 text-xs flex-shrink-0" aria-hidden="true">
                <Icon id={expandedId === p.id ? "ic-chevron-up" : "ic-chevron-down"} size={14} className="inline-block" style={{ color: "currentColor" }} />
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 mt-3 pt-2.5 border-t border-slate-100">
              <span className="text-xs font-medium text-slate-500">{ct("profiles.show_on_device", "Show on this device")}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleVisibility && onToggleVisibility(p.id); }}
                aria-pressed={!hiddenIds?.includes(p.id)}
                title={hiddenIds?.includes(p.id) ? "Hidden on this device — tap to show" : "Visible on this device — tap to hide"}
                className={`flex-shrink-0 inline-block w-9 h-5 rounded-full relative transition-colors ${!hiddenIds?.includes(p.id) ? "bg-emerald-500" : "bg-slate-200"}`}>
                <span className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${!hiddenIds?.includes(p.id) ? "translate-x-4" : "translate-x-0"}`} />
              </button>
            </div>
          </div>
          {expandedId === p.id && (() => {
            const editing = childDraft?.childId === p.id;
            return (
            <>
            {!editing && (
              <div className="flex justify-end mb-3 -mt-1">
                {/* Sole entry point into editing anything on this card — replaces the old
                    pencil-next-to-the-name pattern, which quietly doubled as "edit just the
                    name" AND "enter edit mode for everything else," with no visual hint it
                    did both. One labelled button, one job. While editing, Save/Cancel live
                    ONLY in the floating bar below (Aug 2026) — this same pair used to
                    duplicate there too, which is exactly what caused two indistinguishable
                    "Save changes" buttons to exist on screen simultaneously (a real
                    accessibility problem, not just a test inconvenience). One control, one
                    place, is the fix at the source rather than just renaming around it. */}
                <button className="text-slate-600 text-xs font-bold bg-slate-100 rounded-lg px-3 py-1.5" onClick={() => requestEditChild(p)}>{ct("profiles.edit_button", "Edit")}</button>
              </div>
            )}

            {/* Profile — identity fields. Name lives here now, not inline in the header;
                see the header's own comment above for why. */}
            <div className="bg-slate-50 rounded-xl p-3 mb-2">
              <div className="text-xs font-medium text-slate-500 mb-2">{ct("profiles.section_profile", "Profile")}</div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-500">{ct("profiles.name_label", "Name")}</label>
                {editing ? (
                  <input
                    type="text"
                    className="border border-slate-200 rounded-lg px-2 py-1 text-xs w-32 text-right"
                    value={childDraft.name}
                    onChange={(e) => updateDraftField("name", e.target.value)}
                  />
                ) : (
                  <span className="text-xs text-slate-700 font-semibold">{p.name}</span>
                )}
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-500">{ct("profiles.age_label", "Age")} <span className="text-rose-400">(required)</span></label>
                  {editing ? (
                    <input
                      type="number" min="0" max="25"
                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs w-16 text-center"
                      placeholder="—"
                      value={childDraft.age}
                      onChange={(e) => updateDraftField("age", e.target.value.replace(/[^0-9]/g, ""))}
                    />
                  ) : (
                    <span className="text-xs text-slate-700 font-semibold">{effectiveAge(p) ?? "—"}</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{ct("profiles.age_hint", "Sets the right difficulty for Learn & Earn games")}</div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-500">{ct("profiles.school_year_label", "School year")} <span className="text-slate-300">(optional)</span></label>
                  {editing ? (
                    <select
                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs w-32 text-slate-700"
                      value={childDraft.schoolYear}
                      onChange={(e) => updateDraftField("schoolYear", e.target.value)}
                    >
                      {SCHOOL_YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  ) : (
                    <span className="text-xs text-slate-700 font-semibold">
                      {resolveSchoolYear(p) ?? "—"}
                      {!p.schoolYear && resolveSchoolYear(p) && <span className="text-slate-300 font-normal"> (estimated)</span>}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* This device — genuinely per-device settings only. "Show on this device" stays
                up in the header (Alan's call — it's the fast multi-child scan control and
                doesn't need duplicating down here); this group only adds what isn't already
                visible above. */}
            {onToggleMute && (() => {
              const visible = !hiddenIds?.includes(p.id);
              const notify = !mutedIds?.includes(p.id);
              return (
                <div className="bg-slate-50 rounded-xl p-3 mb-2">
                  <div className="text-xs font-medium text-slate-500 mb-2">{ct("profiles.this_device", "This device")}</div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${visible ? "text-slate-500" : "text-slate-300"}`}>Receive notifications from {p.name}</span>
                    <button
                      type="button"
                      disabled={!visible}
                      onClick={() => onToggleMute(p.id)}
                      aria-pressed={notify}
                      title={!visible ? "Turned off automatically — this child is hidden on this device" : notify ? "Notifications on for this child on this device — tap to turn off" : "Notifications off for this child on this device — tap to turn on"}
                      className={`flex-shrink-0 inline-block w-9 h-5 rounded-full relative transition-colors ${notify && visible ? "bg-emerald-500" : "bg-slate-200"} ${!visible ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <span className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notify && visible ? "translate-x-4" : "translate-x-0"}`} />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Balances — per-child settings about how this child's balances behave.
                (Alan, Aug 2026) The Savings Vault on/off toggle that used to live at the
                bottom of this group is gone: the vault is a permanent part of every child's
                profile now, so there is nothing to switch. What replaces it is the per-child
                INTEREST toggle — a different question ("does this child earn interest?"),
                which the vault toggle had only ever answered as a side effect. Keeping it
                here for the same reason the vault toggle lived here: it's a per-CHILD profile
                setting, identical on every device, not one of the per-device switches in the
                header row.
                (R-B addendum, Resolution 3) The token/vault balance EDIT fields that used to
                sit above this toggle are gone — balance corrections now go through Manage
                balances (credit/debit with a reason, writing a real transaction) in the Kids
                tab's "Do now" group, which is the auditable path the audit's decision was
                actually about. The plain read-only balance line stays in the collapsed header
                above, same as before. */}
            <div className="bg-slate-50 rounded-xl p-3 mb-2">
              <div className="text-xs font-medium text-slate-500 mb-2">{ct("profiles.section_balances", "Balances")}</div>
              {/* Per-child vault interest opt-out (Alan, Aug 2026). This is the control that
                  replaces the old vault on/off toggle, and it is NOT the same question: the
                  vault always exists now, but whether a given child earns interest on it is
                  still the parent's call. It has to exist, because removing the vault toggle
                  would otherwise have silently enrolled every previously-excluded child.
                  The household APR still lives in Settings → Rewards & Currency — this is a
                  per-child opt-out beneath it, not a second copy of that control, so it's
                  disabled (with a reason) whenever the household setting is off rather than
                  appearing active-but-inert. */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <div className="min-w-0 pr-2">
                  <div className="text-xs text-slate-500">{ct("profiles.earns_interest_label", "{name} earns Vault interest", { name: p.name })}</div>
                  <div className="text-xs text-slate-300">
                    {state.settings.vaultInterest?.enabled
                      ? ct("profiles.earns_interest_hint", "Same on every device, not per device")
                      : ct("profiles.earns_interest_household_off", "Turn on Vault interest for the household first")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleEarnsInterest(p.id)}
                  aria-pressed={earnsVaultInterest(p)}
                  disabled={!state.settings.vaultInterest?.enabled}
                  className={`flex-shrink-0 inline-block w-9 h-5 rounded-full relative transition-colors ${!state.settings.vaultInterest?.enabled ? "bg-slate-200 opacity-40" : earnsVaultInterest(p) ? "bg-emerald-500" : "bg-slate-200"}`}
                >
                  <span className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${earnsVaultInterest(p) ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
              {/* Status + deep link into the household setting, never a second control —
                  two controls for one concept in two tabs is a support-ticket generator. */}
              {state.settings.vaultInterest?.enabled && (
                earnsVaultInterest(p) ? (
                  <button type="button" onClick={onOpenHouseholdMoney} className="mt-1.5 text-xs font-semibold text-teal-600 flex items-center gap-1">
                    {ct("profiles.vault_interest_status", "Earning {apr}% a year interest (household setting)", { apr: clampApr(state.settings.vaultInterest.apr) })}
                  </button>
                ) : (
                  <div className="mt-1.5 text-xs text-slate-400">{ct("profiles.vault_interest_off_note", "{name} isn't earning interest", { name: p.name })}</div>
                )
              )}
            </div>

            {editing && editingAvatarFor === p.id && (
              <div className="flex flex-col gap-2 mb-2 bg-slate-50 rounded-xl p-3">
                <div className="text-xs font-medium text-slate-500">{ct("character.pick.title", "Pick your character")}</div>
                <CharacterPicker profile={p} onPick={(id) => { updateChildAvatar(p.id, characterPatch(id)); setEditingAvatarFor(null); }} compact />
              </div>
            )}
            {editing && (
              <button className="text-xs font-semibold text-slate-500 mb-2 -mt-1" onClick={() => { setEditingAvatarFor(p.id); setPhotoError(""); }}>{ct("profiles.change_photo", "Change photo")}</button>
            )}

            {/* Goals editing was removed from here (R-B addendum, Resolution 2 — audit-decided
                "Kills the P3/P9 goals duplication"): GoalsSection (Kids tab's "What {kid} is
                saving for" group) is now the single parent-side goals surface. */}

            {editing && state.profiles.length > 0 && (
              <div className="mt-2 pt-3 border-t border-slate-100 text-left">
                {confirmingDeleteFor === p.id ? (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                    <p className="text-xs text-rose-700 mb-2">This will permanently delete {p.name}'s profile — their balance, goals, and history all go with it. If you add them again later, you'll need to set everything up from scratch.</p>
                    <div className="flex gap-2">
                      <button className="flex-1 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-lg py-1.5" onClick={() => { removeChild(p.id); setConfirmingDeleteFor(null); }}>Yes, delete {p.name}</button>
                      <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setConfirmingDeleteFor(null)}>{ct("profiles.cancel_button", "Cancel")}</button>
                    </div>
                  </div>
                ) : (
                  <button className="text-rose-500 text-xs font-bold" onClick={() => setConfirmingDeleteFor(p.id)}>{ct("profiles.delete_child", "Delete Child from Household")}</button>
                )}
              </div>
            )}
            </>
            );
          })()}
        </div>
      ))}

      {/* Floating Save bar (Aug 2026) — the top Save/Cancel controls can scroll out of view
          on a card with a lot of content (several goals, etc.), and "it's easy to miss the
          save button" was the exact complaint. Sticky rather than fixed: sticks to the
          bottom of the viewport using the same mechanism the app's existing top sticky nav
          already relies on (no nested scroll containers here, so "nearest scrolling
          ancestor" is the window itself), without needing its own fixed-position stacking
          logic. Offset well clear of the fixed Parent Mode bottom tab bar (App.jsx's
          ParentBottomNav, R-B restructure) — that bar is on screen the entire time Parent
          Mode is, including on this exact tab, so without a deliberate gap the two would sit
          on top of each other. Worth an eyeball on staging across a couple of screen sizes
          regardless — exact pixel stacking between two independently-positioned elements is
          not something to fully trust from source alone. */}
      {childDraft && (() => {
        const editingChild = state.profiles.find((p) => p.id === childDraft.childId);
        return (
          <div className="sticky z-30" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)" }}>
            <div className="bg-white border border-slate-200 rounded-2xl shadow-lg px-4 py-3 flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-slate-600 truncate">Editing {editingChild?.name || "profile"}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button disabled={!childDraft?.age?.trim()} className="bg-slate-900 text-white text-xs font-bold rounded-lg px-3 py-1.5 disabled:opacity-50" onClick={saveChildEdits}>{ct("profiles.save_changes", "Save changes")}</button>
                <button className="text-slate-400 text-xs font-bold" onClick={() => { setChildDraft(null); setEditingAvatarFor(null); }}>{ct("profiles.cancel_editing", "Cancel editing")}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Unsaved-changes dialog (Aug 2026) — only one child's edits can be in progress at
          once (childDraft holds a single id), so tapping "Edit" on a DIFFERENT child while
          one is still unsaved would otherwise silently discard it with no warning. Fires
          only for that specific case — see requestEditChild's own comment for why every
          other path (re-editing the same child, starting fresh with nothing in progress)
          skips this entirely. */}
      {unsavedPromptFor && (() => {
        const fromChild = state.profiles.find((p) => p.id === childDraft?.childId);
        const toChild = state.profiles.find((p) => p.id === unsavedPromptFor);
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-5 max-w-xs w-full shadow-2xl text-center">
              <div className="font-bold text-slate-800 mb-2">{ct("unsaved.title", "You have unsaved changes")}</div>
              <p className="text-xs text-slate-500 mb-4">{ct("unsaved.body_switch", "Save or discard your changes to {from} before editing {to}.", { from: fromChild?.name || "this profile", to: toChild?.name || "someone else" })}</p>
              <button
                className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5 mb-2"
                onClick={() => {
                  // saveChildEdits can fail its own validation (e.g. a blank age) — its
                  // toast already explains why, so just don't switch children on failure,
                  // leaving the parent looking at the error on the form they were already on.
                  const ok = saveChildEdits();
                  if (ok && toChild) startEditChild(toChild);
                  setUnsavedPromptFor(null);
                }}
              >{ct("profiles.save_and_continue", "Save and continue")}</button>
              <button
                className="w-full bg-rose-50 text-rose-600 font-bold text-sm rounded-xl py-2.5 mb-2"
                onClick={() => {
                  setChildDraft(null);
                  setEditingAvatarFor(null);
                  if (toChild) startEditChild(toChild);
                  setUnsavedPromptFor(null);
                }}
              >{ct("profiles.discard_changes", "Discard changes")}</button>
              <button className="w-full text-slate-400 font-semibold text-xs py-1.5" onClick={() => setUnsavedPromptFor(null)}>Keep editing {fromChild?.name || ""}</button>
            </div>
          </div>
        );
      })()}

      {!showAdd ? (
        <button className="bg-pill text-navy font-bold text-sm rounded-lg py-3" onClick={() => setShowAdd(true)}>{ct("profiles.add_child_button", "+ Add child profile")}</button>
      ) : (
        <div ref={addFormRef} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" style={{ scrollMarginTop: "8px" }} data-testid="add-child-form">
          <div className="flex items-center gap-2 mb-2">
            <label htmlFor={addChildNameId} className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">Child's name</label>
            <input id={addChildNameId} className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <CharacterPicker value={character} onPick={setCharacter} compact />
          <div className="flex items-center gap-2 mb-2">
            <label htmlFor={addAgeId} className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("profiles.age_label", "Age")} <span className="text-rose-400 font-normal">(required)</span></label>
            <input
              id={addAgeId}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0"
              inputMode="numeric"
              value={age}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                setAge(v);
                // Live estimate, same as the per-child edit dropdown's default — only while
                // the parent hasn't picked a school year themselves yet (see the field
                // below), so a later age correction can't silently overwrite their choice.
                if (!addSchoolYearTouched) {
                  const est = estimateSchoolYear(v === "" ? NaN : parseInt(v, 10));
                  if (est) setAddSchoolYear(est);
                }
              }}
              placeholder="Sets the right difficulty level"
            />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <label htmlFor={addSchoolYearId} className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("profiles.school_year_label", "School year")} <span className="text-slate-400 font-normal">(optional)</span></label>
            <select
              id={addSchoolYearId}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0"
              value={addSchoolYear}
              onChange={(e) => { setAddSchoolYear(e.target.value); setAddSchoolYearTouched(true); }}
            >
              {SCHOOL_YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {!hideStartingBalance && (
            <div className="flex items-center gap-2 mb-3">
              <label htmlFor={addStartBalId} className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("profiles.starting_balance_label", "Starting Token Balance")}</label>
              <input id={addStartBalId} className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" type="number" value={startBal} onChange={(e) => setStartBal(e.target.value)} />
            </div>
          )}
          <div className="flex gap-2">
            <button disabled={!name.trim() || !age.trim()} className="flex-1 bg-slate-900 text-white font-bold text-sm rounded-lg py-2 disabled:opacity-50" onClick={addChild}>{ct("profiles.add_button", "Add")}</button>
            <button className="px-3 text-slate-400 text-sm" onClick={() => setShowAdd(false)}>{ct("profiles.cancel_button", "Cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
});

/* ------------------------- Learn & Earn admin (parent) --------------------- */
